/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { FeatureFlag, WorkspaceData } from '../types';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';
import { TabPanel } from '../../../../../components/tabpanel/TabPanel';
import { Alert, createStyles, Theme } from '@mui/material';
import { NewWorkspaceCard } from './NewWorkspaceCard';
import { TagsInput } from './TagsInput';
import { usePermission } from '@backstage/plugin-permission-react';
import { featureFlagsProductionUpdatePermission } from '@internal/backstage-plugin-feature-flags-manager-common';

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: '#a0a0a0',
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  }),
)(TableCell);

export const FeaturesManagementCard = (props: {
  selectedFlag: FeatureFlag;
  onUpdate: (flag: FeatureFlag) => void;
}) => {
  const { selectedFlag, onUpdate } = props;

  const [tabValue, setTabValue] = useState('DEV');
  const [envWorkspaces, setEnvWorkspaces] = useState<WorkspaceData[]>([]);
  const [modalAddWorkspacesOpen, setModalAddWorkspacesOpen] = useState(false);
  const [modalStatusConfirmationOpen, setModalStatusConfirmationOpen] =
    useState(false);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertMsg, setErrorAlertMsg] = useState('');

  const allowedProductionFlagsPermission = usePermission({
    permission: featureFlagsProductionUpdatePermission,
  }).allowed;

  const processWorkspaces = (environment: string) => {
    const currentEnvironment = selectedFlag.environments?.find(
      env => env.environment === environment,
    );
    setEnvWorkspaces(currentEnvironment ? currentEnvironment.data : []);
  };

  useEffect(() => {
    processWorkspaces(tabValue);
  }, [selectedFlag]);

  const handleTabChange = (newTabValue: string) => {
    setTabValue(newTabValue);
    processWorkspaces(newTabValue);
  };

  const handleWorkspaceChange = (workspaces: WorkspaceData[]) => {
    let updatedEnvironments =
      selectedFlag.environments?.map(env => {
        if (env.environment === tabValue) {
          return { ...env, data: workspaces };
        }
        return env;
      }) || [];

    const existingEnvironment = selectedFlag.environments?.some(
      env => env.environment === tabValue,
    );
    const newEnvironment = {
      environment: tabValue as 'PRD' | 'STG' | 'DEV',
      data: workspaces,
    };
    if (!existingEnvironment) {
      updatedEnvironments = [...updatedEnvironments, newEnvironment];
    }

    onUpdate({ ...selectedFlag, environments: updatedEnvironments });
  };

  function handleNewWorkspaces(workspaces: WorkspaceData[]): void {
    let newWorkspaceList = envWorkspaces;
    workspaces.forEach(workspace => {
      const existingWorkspace = envWorkspaces.find(
        value => value.workspace === workspace.workspace,
      );
      if (existingWorkspace) {
        setErrorAlertMsg(`Workspace '${workspace.workspace}' already exists.`);
        setOpenErrorAlert(true);
        setModalAddWorkspacesOpen(false);
        return;
      }
      newWorkspaceList = [workspace, ...newWorkspaceList];
    });
    handleWorkspaceChange(newWorkspaceList);
    setEnvWorkspaces(newWorkspaceList);
    setModalAddWorkspacesOpen(false);
  }

  function enableFlagForAllWorkspace(): void {
    const allWorkspacesFlag = {
      workspace: '*',
      enabled: true,
      account: { name: 'all' },
    };
    const existingWorkspace = envWorkspaces.find(
      workspace => workspace.workspace === allWorkspacesFlag.workspace,
    );
    if (!existingWorkspace) {
      const disabledWorkspaces = envWorkspaces.map(workspace => {
        return { ...workspace, enabled: false };
      });
      const newWorkspaceList = [allWorkspacesFlag, ...disabledWorkspaces];
      setEnvWorkspaces(newWorkspaceList);
      handleWorkspaceChange(newWorkspaceList);
    }
  }

  const handleChangeFlagDescription = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onUpdate({ ...selectedFlag, description: event.target.value });
  };

  const changeFlagStatus = (state: boolean) => {
    onUpdate({ ...selectedFlag, state: state ? 'ACTIVE' : 'INACTIVE' });
    setModalStatusConfirmationOpen(false);
  };

  const handleChangeFlagStatus = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const state = event.target.checked;
    if (!state) {
      setModalStatusConfirmationOpen(true);
    } else {
      changeFlagStatus(true);
    }
  };

  const handleWorkspaceStatusChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    workspace: WorkspaceData,
  ) => {
    const updatedWorkspaces = envWorkspaces.map(row => {
      if (row.workspace === workspace.workspace) {
        return { ...row, enabled: event.target.checked };
      }
      return row;
    });
    setEnvWorkspaces(updatedWorkspaces);
    handleWorkspaceChange(updatedWorkspaces);
  };

  const disabledByProductionFlag = () => {
    return !allowedProductionFlagsPermission && tabValue === 'PRD';
  };

  return (
    <>
      <Card style={{ flexGrow: 1 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h5" gutterBottom>
              {selectedFlag?.code}
            </Typography>
            <Box display="flex" alignItems="center" marginRight={2}>
              <Switch
                checked={selectedFlag.state === 'ACTIVE'}
                onChange={handleChangeFlagStatus}
                color="secondary"
                disabled={!allowedProductionFlagsPermission}
              />
              <h3>
                {selectedFlag.state === 'ACTIVE' ? 'Enabled' : 'Disabled'}
              </h3>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gridRowGap={24}>
            <TextField
              label="Intent"
              variant="outlined"
              multiline
              maxRows={4}
              fullWidth
              value={selectedFlag?.description ?? ''}
              inputProps={{ maxLength: 4000 }}
              onChange={handleChangeFlagDescription}
            />
            <TagsInput
              tags={selectedFlag.tags ?? []}
              onChange={tags => onUpdate({ ...selectedFlag, tags })}
            />
            <Tabs
              value={tabValue}
              onChange={(_event, newTabValue) => handleTabChange(newTabValue)}
            >
              <Tab label="DEVELOPMENT" value="DEV" />
              <Tab label="STAGING" value="STG" />
              <Tab label="PRODUCTION" value="PRD" />
            </Tabs>

            <TabPanel value={tabValue} index={tabValue}>
              <Box display="flex" flexDirection="column" gridRowGap={16}>
                <Box display="flex" gridColumnGap={8}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setModalAddWorkspacesOpen(true)}
                    disabled={disabledByProductionFlag()}
                  >
                    Add workspaces
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={enableFlagForAllWorkspace}
                    disabled={disabledByProductionFlag()}
                  >
                    Enable for all workspaces
                  </Button>
                </Box>

                <Box
                  height={250}
                  style={{ overflow: 'auto', scrollbarWidth: 'thin' }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell width={400}>
                          <b>Workspace</b>
                        </StyledTableCell>
                        <StyledTableCell width={400}>
                          <b>Cliente</b>
                        </StyledTableCell>
                        <StyledTableCell align="center" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {envWorkspaces.map(row => (
                        <TableRow key={row.workspace + tabValue}>
                          <TableCell component="th" scope="row">
                            {row.workspace}
                          </TableCell>
                          <TableCell>{row.account?.name}</TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={row.enabled}
                              onChange={event =>
                                handleWorkspaceStatusChange(event, row)
                              }
                              color="secondary"
                              size="small"
                              disabled={disabledByProductionFlag()}
                            />
                            {row.enabled ? 'Enabled' : 'Disabled'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </TabPanel>
          </Box>
        </CardContent>
      </Card>

      {modalAddWorkspacesOpen && (
        <Dialog
          open={modalAddWorkspacesOpen}
          maxWidth="lg"
          onClose={() => setModalAddWorkspacesOpen(false)}
        >
          <DialogTitle>Add workspaces</DialogTitle>
          <DialogContent>
            <NewWorkspaceCard
              handleNewWorkspaces={handleNewWorkspaces}
              env={tabValue}
            />
          </DialogContent>
        </Dialog>
      )}

      {modalStatusConfirmationOpen && (
        <Dialog
          open={modalStatusConfirmationOpen}
          maxWidth="lg"
          onClose={() => setModalStatusConfirmationOpen(false)}
        >
          <DialogTitle>Disable feature flag</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to disable this feature flag? <br />
              This action will{' '}
              <strong> disable the feature for all workspaces</strong>.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setModalStatusConfirmationOpen(false)}
              color="primary"
            >
              Cancel
            </Button>
            <Button onClick={() => changeFlagStatus(false)} color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={openErrorAlert}
        onClose={() => setOpenErrorAlert(false)}
        autoHideDuration={5000}
      >
        <Alert onClose={() => setOpenErrorAlert(false)} severity="error">
          {errorAlertMsg}
        </Alert>
      </Snackbar>
    </>
  );
};
